/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import ReducerConstants from '../../../_shared/constants/reducers';
import renderLocale from '../../../_shared/helpers/renderLocale';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import { TradeState, TradeStateName } from '../../../_shared/types/trade';
import { BidSource, BidStatus } from '../../../_shared/types/auction';
import Toggle from '../../../_shared/components/Toggle';
import MixpanelService from '../../../services/MixpanelService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import DealerSelectDropdown from '../../Vdp/DealerSelectDropdown';

import {
  StyledBidForm,
  BiddingContainer,
  StyledInputs,
  StyledFormItem,
  StyledControls,
  StyledProxyDescription,
  StyledEditButton
} from './Styles';

const propTypes = {
  bidAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  proxyAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minBidAmount: PropTypes.number,
  minProxyAmount: PropTypes.number,
  isAuctionEnded: PropTypes.bool,
  bidIncrements: PropTypes.number,
  proxyIncrements: PropTypes.number,
  formErrors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool])),
  bidStatus: PropTypes.number,
  isEditing: PropTypes.bool.isRequired,
  onSetIsEditingChange: PropTypes.func.isRequired,
  onBidAmountChange: PropTypes.func.isRequired,
  onProxyAmountChange: PropTypes.func.isRequired,
  onProxyToggleChange: PropTypes.func.isRequired,
  bidContext: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.object,
      PropTypes.array
    ])
  )
};

const defaultProps = {
  bidAmount: undefined,
  proxyAmount: undefined,
  minBidAmount: undefined,
  minProxyAmount: undefined,
  isAuctionEnded: false,
  bidIncrements: undefined,
  proxyIncrements: undefined,
  formErrors: {},
  bidStatus: 1,
  bidContext: {}
};

const BidForm = ({
  bidAmount,
  bidContext,
  bidIncrements,
  bidStatus,
  formErrors,
  hasInvalidDealer,
  isAuctionEnded,
  isEditing,
  isMultiOrgAndEnabled,
  minBidAmount,
  minProxyAmount,
  onBidAmountChange,
  onProxyAmountChange,
  onProxyToggleChange,
  onSetIsEditingChange,
  proxyAmount,
  proxyIncrements
}) => {
  const mixpanel = MixpanelService.get();
  const {
    auctionSummary,
    bidSource,
    currentBidAmount,
    currentProxyAmount,
    id,
    isSettingProxy,
    tradeState
  } = bidContext;
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const userLocale = renderLocale(user?.auth);
  const localeVariant = userLocale ? userLocale.substring(0, 2) : null;
  const StartingBidMessage = () => {
    return (
      <>
        {currentBidAmount &&
        (bidStatus === BidStatus.topBidder ||
          bidStatus === BidStatus.proxy ||
          currentProxyAmount) ? (
          <FormattedMessage id="vdp.bid.currentBidAmount" />
        ) : (
          <FormattedMessage id="vdp.bid.startingBid" />
        )}
      </>
    );
  };

  const onEditBidAmount = () => {
    onSetIsEditingChange(true);

    // the logic is a bit tied up currently for the bidSources, this mapping
    // is needed to send the correct option to mixpanel
    const bidSourceMapping = {
      [BidSource.VDP_BIDBOX]: BidSource.VDP_OVERLAY,
      [BidSource.MINI_VDP_BIDBOX]: BidSource.MINI_VDP
    };

    mixpanel.track(MixpanelConfig.names.editBid, {
      trade_id: id,
      auction_id: auctionSummary?.id,
      trade_state: TradeStateName[tradeState],
      source:
        bidSource in bidSourceMapping ? bidSourceMapping[bidSource] : bidSource
    });
  };

  const biddingDealerId = useMemo(() => {
    if (
      tradeState === TradeState.upcoming ||
      auctionSummary?.userBidStatus !== BidStatus.none
    ) {
      return auctionSummary?.biddingDealerId;
    }
    return null;
  }, [
    auctionSummary?.biddingDealerId,
    auctionSummary?.userBidStatus,
    tradeState
  ]);

  return (
    <StyledBidForm className="notranslate">
      {tradeState !== TradeState.pending && isMultiOrgAndEnabled && (
        <DealerSelectDropdown
          biddingDealerId={biddingDealerId}
          hasError={hasInvalidDealer}
        />
      )}
      <BiddingContainer>
        <StyledInputs>
          <StyledFormItem variant={localeVariant}>
            <StyledFormItem.Label variant={formErrors.ERROR_BID && 'error'}>
              {isSettingProxy ? (
                <StartingBidMessage />
              ) : (
                <FormattedMessage id="vdp.bid.bidAmount" />
              )}
            </StyledFormItem.Label>
            {isSettingProxy &&
            tradeState !== TradeState.upcoming &&
            (bidStatus === BidStatus.topBidder ||
              bidStatus === BidStatus.proxy ||
              currentProxyAmount) ? (
              isEditing || bidStatus === BidStatus.outbid ? (
                <StyledFormItem.Input
                  type="number"
                  data-testid="bid-amount"
                  min={minBidAmount}
                  step={bidIncrements}
                  value={bidAmount ? String(bidAmount) : ''}
                  disabled={isAuctionEnded}
                  placeholder={`Minimum ${convertToCurrency(minBidAmount)}`}
                  variant={formErrors.ERROR_BID && 'error'}
                  onChange={e => {
                    onBidAmountChange(e.target.value);
                    if (e.target.value) onSetIsEditingChange(true);
                  }}
                />
              ) : (
                <StyledFormItem.Label>
                  {convertToCurrency(currentBidAmount)}
                  <StyledEditButton
                    testId="bid-amount-edit"
                    onClick={onEditBidAmount}
                  >
                    <FormattedMessage id="vdp.bid.editBidAmount" />
                  </StyledEditButton>
                </StyledFormItem.Label>
              )
            ) : (
              <StyledFormItem.Input
                type="number"
                data-testid={isSettingProxy ? 'starting-bid-txt' : 'bid-amount'}
                min={minBidAmount}
                step={bidIncrements}
                value={bidAmount ? String(bidAmount) : ''}
                disabled={isAuctionEnded}
                placeholder={`Minimum ${convertToCurrency(minBidAmount)}`}
                variant={formErrors.ERROR_BID && 'error'}
                onChange={e => {
                  onBidAmountChange(e.target.value);
                }}
              />
            )}
          </StyledFormItem>
          {isSettingProxy && (
            <StyledFormItem>
              <StyledFormItem.Label variant={formErrors.ERROR_PROXY && 'error'}>
                {/* Proxy Amount */}
                <FormattedMessage id="vdp.bid.proxyAmount" />
              </StyledFormItem.Label>
              <StyledFormItem.Input
                data-testid="proxy-bid-text"
                type="number"
                min={isEditing ? minBidAmount + bidIncrements : minProxyAmount}
                step={proxyIncrements}
                value={proxyAmount ? String(proxyAmount) : ''}
                disabled={isAuctionEnded}
                placeholder={
                  isEditing
                    ? `Minimum ${convertToCurrency(
                        minBidAmount + bidIncrements
                      )}`
                    : `Minimum ${convertToCurrency(minProxyAmount)}`
                }
                variant={formErrors.ERROR_PROXY && 'error'}
                onChange={e => {
                  onProxyAmountChange(e.target.value);
                }}
              />
            </StyledFormItem>
          )}
        </StyledInputs>
        <StyledControls>
          {isSettingProxy && (
            <StyledFormItem variant={localeVariant}>
              <StyledProxyDescription>
                {/* We’ll bid just enough to keep you the top bidder, and stop at your proxy amount. */}
                <FormattedMessage id="vdp.bid.proxyMessage" />
              </StyledProxyDescription>
            </StyledFormItem>
          )}
          {tradeState === TradeState.active && !currentProxyAmount && (
            <StyledFormItem>
              <StyledFormItem.Label>
                {/* Set a proxy amount */}
                <FormattedMessage id="vdp.bidModal.form.setProxy" />
              </StyledFormItem.Label>
              <StyledFormItem.Wrapper>
                <Toggle
                  testId="bidModal-proxy-toggle"
                  onClick={onProxyToggleChange}
                  isChecked={isSettingProxy}
                />
              </StyledFormItem.Wrapper>
            </StyledFormItem>
          )}
        </StyledControls>
      </BiddingContainer>
    </StyledBidForm>
  );
};

BidForm.propTypes = propTypes;
BidForm.defaultProps = defaultProps;

export default BidForm;
